/*
 * tiktok-variant-picker.js
 *
 * Bridges the custom [data-tt-variants] picker to the native Shopify
 * variant-picker (`variant-radios` / `variant-selects`) so that:
 *   - Dawn's variant-change cascade fires (price, inventory, sections refresh)
 *   - The hidden form `id` input is set by Dawn (we don't fight it)
 *   - Apps that listen to the native picker (Recharge Subscription Widget,
 *     etc.) receive the variant change and update their UI accordingly
 *
 * We achieve this by checking the matching native input(s) for each option
 * value of the chosen variant, then dispatching a `change` event on each
 * input. The native variant-radios web component handles the rest.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.ttInit === '1') return;
    root.dataset.ttInit = '1';

    var options = root.querySelectorAll('[data-tt-variant]');

    // A lire maintenant, et pas plus tard : selectOption ecrit ?variant= dans
    // l'URL, donc apres le premier appel on ne saurait plus distinguer un choix
    // venu du visiteur d'une valeur posee par ce script.
    var visitorChoseVariant = false;
    try {
      visitorChoseVariant = !!new URL(window.location.href).searchParams.get('variant');
    } catch (e) {}
    // Le sélecteur est rendu hors du <form> d'achat : on le retrouve par son id.
    var form    = root.closest('form[action*="/cart/add"]') ||
                  (root.dataset.formId ? document.getElementById(root.dataset.formId) : null);

    // Find every native picker on the page (not just inside our form),
    // covering Dawn, gem-pages, and custom button pickers.
    function findNativePickers() {
      return Array.prototype.slice.call(document.querySelectorAll(
        'variant-radios, variant-selects, [data-product-options], gp-variant-selected, .product-variants, .variant-input-wrap'
      ));
    }

    function normalizeText(s) {
      return (s || '').replace(/\s+/g, ' ').replace(/[›»\u203a]/g, '').trim().toLowerCase();
    }

    /*
     * Try every known shape until one matches:
     *   A. <fieldset> grouping per option with radio inputs (Dawn)
     *   B. <select> per option (Dawn variant-selects)
     *   C. <input type="radio" name="options[...]"> not nested in fieldsets
     *   D. Any clickable element (button, label, a, [role="button"]) whose
     *      visible text matches the option value (custom pill pickers)
     */
    function syncNativePicker(variantOptions) {
      if (!variantOptions.length) return false;
      var pickers = findNativePickers();
      var anySynced = false;

      pickers.forEach(function (picker) {
        // A — fieldsets
        var fieldsets = picker.querySelectorAll('fieldset');
        if (fieldsets.length) {
          variantOptions.forEach(function (value, idx) {
            var fs = fieldsets[idx];
            if (!fs) return;
            fs.querySelectorAll('input[type="radio"]').forEach(function (r) {
              if (r.value === value && !r.checked) {
                r.checked = true;
                r.dispatchEvent(new Event('change', { bubbles: true }));
                anySynced = true;
              }
            });
          });
          if (anySynced) return;
        }

        // B — selects
        var selects = picker.querySelectorAll('select');
        if (selects.length) {
          variantOptions.forEach(function (value, idx) {
            var sel = selects[idx];
            if (!sel) return;
            if (sel.value !== value) {
              sel.value = value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
              anySynced = true;
            }
          });
          if (anySynced) return;
        }

        // C — flat radios
        var radios = picker.querySelectorAll('input[type="radio"]');
        if (radios.length) {
          variantOptions.forEach(function (value) {
            radios.forEach(function (r) {
              if (r.value === value && !r.checked) {
                r.checked = true;
                r.dispatchEvent(new Event('change', { bubbles: true }));
                anySynced = true;
              }
            });
          });
          if (anySynced) return;
        }

        // D — clickable pill buttons matched by visible text
        var clickables = picker.querySelectorAll('button, label, a, [role="button"]');
        variantOptions.forEach(function (value) {
          var target = normalizeText(value);
          for (var i = 0; i < clickables.length; i++) {
            var el = clickables[i];
            if (normalizeText(el.textContent) === target) {
              el.click();
              anySynced = true;
              break;
            }
          }
        });
      });

      return anySynced;
    }

    // Le champ id du formulaire est ce qui part au panier : il doit toujours
    // correspondre au pack choisi, sélecteur natif présent ou non (sans lui,
    // le panier recevait le pack d'origine). Dawn y écrit la même valeur.
    function fallbackSetHiddenId(variantId) {
      if (!form) return;
      // getAttribute : form.id renverrait le champ name="id" du formulaire.
      var formId = form.getAttribute('id') || '';
      if (formId) {
        var installment = document.getElementById(formId.replace('product-form-', 'product-form-installment-'));
        var instInput = installment && installment.querySelector('input[name="id"]');
        if (instInput) instInput.value = variantId;
      }
      var input = form.querySelector('input[name="id"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'id';
        form.appendChild(input);
      }
      if (input.value !== variantId) {
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function updateUrl(variantId) {
      if (!('URL' in window) || !window.history || !history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('variant') === variantId) return;
        url.searchParams.set('variant', variantId);
        history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    // ---- Details des lignes -------------------------------------------------
    // Prix au mille et phrase « Pour X de plus, passe a … » relus dans les
    // prix affiches : ils suivent la remise d'abonnement et une appli de
    // devises eventuelle.

    // « €71,99 », « 71,99 € », « $1,234.50 » -> 71.99, 71.99, 1234.5
    function parseMoney(text) {
      var m = String(text || '').match(/\d[\d\s.,  ]*/);
      if (!m) return null;
      var raw = m[0].replace(/[\s  ]+$/, '');
      var dec = /[.,]\d{1,2}$/.test(raw) ? raw.charAt(raw.search(/[.,]\d{1,2}$/)) : '';
      var num = raw.replace(/[^\d.,]/g, '');
      if (dec) {
        var parts = num.split(dec);
        var frac = parts.pop();
        num = parts.join('').replace(/[.,]/g, '') + '.' + frac;
      } else {
        num = num.replace(/[.,]/g, '');
      }
      var n = parseFloat(num);
      return isNaN(n) ? null : { value: n, raw: raw, dec: dec || ',' };
    }

    // Ecrit un montant dans la mise en forme d'un prix existant.
    function formatLike(sample, value) {
      var p = parseMoney(sample);
      if (!p) return value.toFixed(2);
      return sample.replace(p.raw, value.toFixed(2).replace('.', p.dec));
    }

    function shownPrice(opt) {
      var el = opt.querySelector('.ob-sub__now') || opt.querySelector('.tt-variants__price');
      return el ? el.textContent.trim() : '';
    }

    var tplPer = root.getAttribute('data-tpl-per') || '';
    var tplNext = root.getAttribute('data-tpl-next') || '';
    var nextBtn = root.querySelector('[data-tt-next]');
    var nextTarget = null;

    function updateRows() {
      options.forEach(function (opt) {
        var per = opt.querySelector('[data-tt-per]');
        var qty = parseInt(opt.getAttribute('data-qty'), 10);
        if (!per || !(qty > 0) || !tplPer) return;
        var text = shownPrice(opt);
        var p = parseMoney(text);
        if (!p) return;
        var t = tplPer.replace('%P', formatLike(text, p.value * 1000 / qty));
        if (per.textContent !== t) per.textContent = t;
      });

      if (!nextBtn) return;
      var cur = root.querySelector('.tt-variants__option.is-selected');
      var curQty = cur ? parseInt(cur.getAttribute('data-qty'), 10) : 0;
      nextTarget = null;
      if (cur && curQty > 0 && tplNext) {
        options.forEach(function (o) {
          var q = parseInt(o.getAttribute('data-qty'), 10);
          if (q > curQty && !o.classList.contains('is-unavailable') &&
              (!nextTarget || q < parseInt(nextTarget.getAttribute('data-qty'), 10))) nextTarget = o;
        });
      }
      var a = cur && parseMoney(shownPrice(cur));
      var b = nextTarget && parseMoney(shownPrice(nextTarget));
      if (a && b && b.value > a.value) {
        nextBtn.textContent = tplNext
          .replace('%A', formatLike(shownPrice(cur), b.value - a.value))
          .replace('%Q', nextTarget.getAttribute('data-label') || '');
        nextBtn.hidden = false;
      } else {
        nextBtn.hidden = true;
      }
    }

    var rowsPending = null;
    function queueRows() {
      if (rowsPending) return;
      rowsPending = setTimeout(function () { rowsPending = null; updateRows(); }, 30);
    }
    if ('MutationObserver' in window) {
      // Prix reecrits par le bloc d'abonnement ou une appli de devises.
      new MutationObserver(queueRows).observe(root.querySelector('.tt-variants__list') || root, {
        childList: true, subtree: true, characterData: true
      });
    }
    document.addEventListener('tt:variant:change', queueRows);
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (nextTarget) selectOption(nextTarget);
      });
    }

    // « Voir tous les packs » : la liste est repliee au chargement ; le pack
    // choisi reste toujours visible, meme s'il fait partie des masques.
    var moreBtn = root.querySelector('[data-tt-more]');
    if (moreBtn) {
      var moreLabel = moreBtn.querySelector('span');
      var closedText = moreLabel ? moreLabel.textContent : '';
      var openText = moreBtn.getAttribute('data-label-open') || closedText;
      root.classList.add('is-collapsed');
      moreBtn.addEventListener('click', function () {
        var open = root.classList.toggle('is-collapsed') === false;
        moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (moreLabel) moreLabel.textContent = open ? openText : closedText;
      });
    }

    function selectOption(opt) {
      if (opt.classList.contains('is-unavailable')) return;

      var variantId = opt.dataset.variantId;
      if (!variantId) return;

      // Visual state
      options.forEach(function (o) { o.classList.remove('is-selected'); });
      opt.classList.add('is-selected');

      var radio = opt.querySelector('[data-tt-radio]');
      if (radio) radio.checked = true;

      // Drive the native picker if present (preferred path)
      var variantOptions = (opt.dataset.variantOptions || '').split('||').filter(Boolean);
      syncNativePicker(variantOptions);
      fallbackSetHiddenId(variantId);

      // Keep ?variant=… in sync so refresh / share / back-forward all work,
      // and any app reading the URL stays correct.
      updateUrl(variantId);

      root.dispatchEvent(new CustomEvent('tt:variant:change', {
        bubbles: true,
        detail: { variantId: variantId }
      }));
      updateRows();
    }

    options.forEach(function (opt) {
      opt.addEventListener('click', function (e) {
        if (e.target.tagName === 'INPUT') return;
        e.preventDefault();
        selectOption(opt);
      });
      opt.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          selectOption(opt);
        }
      });
      // Clavier : les fleches cochent le bouton radio du palier voisin sans
      // passer par un clic. selectOption coche par programme, ce qui n'emet
      // pas de change : pas de boucle.
      var radio = opt.querySelector('[data-tt-radio]');
      if (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) selectOption(opt);
        });
      }
    });

    // Mirror native pickers → our cards (so external clicks stay in sync).
    findNativePickers().forEach(function (picker) {
      picker.addEventListener('change', function () {
        // Read the current variant id from the URL (Dawn updates ?variant=…
        // on every change), or fall back to the form's hidden input.
        var currentId = null;
        try {
          currentId = new URL(window.location.href).searchParams.get('variant');
        } catch (e) {}
        if (!currentId && form) {
          var idInput = form.querySelector('input[name="id"]');
          if (idInput) currentId = idInput.value;
        }
        if (!currentId) return;
        options.forEach(function (o) {
          o.classList.toggle('is-selected', o.dataset.variantId === currentId);
        });
        updateRows();
      });
    });

    // Synchronisation initiale : la carte selectionnee doit correspondre a
    // l'identifiant soumis par le formulaire.
    //
    // Si la section demande de mettre en avant un palier et que le visiteur
    // n'a pas lui-meme demande une variante, c'est ce palier qui est
    // selectionne a l'arrivee. On passe par selectOption plutot que par une
    // classe posee en Liquid : tout le reste de la page (libelle du bouton,
    // bloc d'abonnement, applications tierces) lit l'etat du theme, et seul
    // ce chemin le met reellement a jour.
    var featured = root.hasAttribute('data-tt-preselect-best')
      ? root.querySelector('.tt-variants__option.is-best:not(.is-unavailable)')
      : null;

    var initial = (!visitorChoseVariant && featured) ||
                  root.querySelector('.tt-variants__option.is-selected') ||
                  root.querySelector('.tt-variants__option:not(.is-unavailable)');
    if (initial) selectOption(initial);
  }

  function initAll() {
    document.querySelectorAll('[data-tt-variants]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
